import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";

export interface PlaySummary {
  id: string;
  archetype: string;
  state: string;
  workspaceId?: string;
  ownerUserId?: string | null;
  targetWindowEnd?: string | null;
  startedAt?: string | null;
  [k: string]: unknown;
}

export interface PlayListResponse {
  items: PlaySummary[];
  nextCursor: string | null;
}

export function useRunningPlays(workspaceId: string) {
  return useQuery<PlayListResponse, Error>({
    queryKey: ["plays", workspaceId, "running"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<PlayListResponse>(
        `/api/workspaces/${workspaceId}/plays?state=running`,
      ),
  });
}

export function useReplanningPlays(workspaceId: string) {
  return useQuery<PlayListResponse, Error>({
    queryKey: ["plays", workspaceId, "replanning"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<PlayListResponse>(
        `/api/workspaces/${workspaceId}/plays?state=replanning`,
      ),
  });
}

export interface PendingDecisionItem {
  step: {
    id: string;
    playId: string;
    title: string;
    state: string;
    ord: number;
    type: string;
    ownerType: string;
    requiresSignoff: boolean;
  };
  play: {
    id: string;
    archetype: string;
    state: string;
    workspaceId: string;
    workspaceName: string;
    targetWindowEnd: string | null;
  };
}

export interface PendingDecisionsResponse {
  items: PendingDecisionItem[];
}

export function useMyPendingDecisions(workspaceId: string) {
  return useQuery<PendingDecisionsResponse, Error>({
    queryKey: ["me-steps", "awaiting_review", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const r = await apiGet<PendingDecisionsResponse>(
        `/api/me/steps?state=awaiting_review&reviewer=true`,
      );
      return {
        items: r.items.filter((it) => it.play.workspaceId === workspaceId),
      };
    },
  });
}

interface RoadmapListEnvelope {
  entries: unknown[];
  total: number;
}

export function useRoadmapInboxCount(workspaceId: string) {
  return useQuery<number, Error>({
    queryKey: ["roadmap", workspaceId, "count-unplanned"],
    enabled: !!workspaceId,
    queryFn: async () => {
      const r = await apiGet<RoadmapListEnvelope>(
        `/api/workspaces/${workspaceId}/roadmap?status=committed`,
      );
      return r.total;
    },
  });
}
